import { asRecord } from '@/components/agent-chat/as-record.util';
import type { CleanupObject } from '@/types/custom-playbooks';
import type {
  DuplicateGroupsSummaryDto,
  DuplicateHub,
  DuplicateReviewStatus,
} from '@/types/duplicate-groups';

/**
 * The review summary as the screen reads it (W5 review contract,
 * `docs/plans/agents-w5-review-api-contract-2026-09-25.md`, Summary). The
 * store reads every answer through this, so a summary missing a field reads
 * as zero or no, never as a crash: on 2026-09-25 the live test's server sent
 * `openByObject` and `viewer.canDecide`, and the screen threw on
 * `byObject.company`. Merge stays off unless `canDecide` is exactly true.
 */

const STATUSES: readonly DuplicateReviewStatus[] = [
  'open',
  'merging',
  'merged',
  'skipped',
  'failed',
  'not_duplicates',
];

const OBJECTS: readonly CleanupObject[] = [
  'account',
  'company',
  'contact',
  'lead',
];

function whole(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

function hubOf(value: unknown): DuplicateHub | null {
  const hub = asRecord(value) ?? {};
  if (
    typeof hub.object !== 'string' ||
    !(OBJECTS as readonly string[]).includes(hub.object) ||
    typeof hub.kind !== 'string' ||
    typeof hub.value !== 'string'
  )
    return null;
  return {
    object: hub.object as CleanupObject,
    kind: hub.kind,
    value: hub.value,
    count: whole(hub.count),
  };
}

export function duplicateSummaryOf(raw: unknown): DuplicateGroupsSummaryDto {
  const data = asRecord(raw) ?? {};
  const byStatus = asRecord(data.byStatus) ?? {};
  const byObject = asRecord(data.byObject) ?? {};
  const byConfidence = asRecord(data.byConfidence) ?? {};
  const objects: Partial<Record<CleanupObject, number>> = {};
  for (const object of OBJECTS) {
    const count = whole(byObject[object]);
    if (count > 0) objects[object] = count;
  }
  const connector = data.connectorName;
  return {
    byStatus: Object.fromEntries(
      STATUSES.map((status) => [status, whole(byStatus[status])])
    ) as Record<DuplicateReviewStatus, number>,
    byObject: objects,
    byConfidence: {
      exact: whole(byConfidence.exact),
      possible: whole(byConfidence.possible),
    },
    flagged: whole(data.flagged),
    hubs: (Array.isArray(data.hubs) ? data.hubs : [])
      .map(hubOf)
      .filter((hub): hub is DuplicateHub => hub !== null),
    truncated: data.truncated === true,
    leadsAreContacts: whole(data.leadsAreContacts),
    crmType:
      data.crmType === 'salesforce' || data.crmType === 'hubspot'
        ? data.crmType
        : null,
    canDecide: data.canDecide === true,
    connectorName:
      typeof connector === 'string' && connector.trim() ? connector : null,
  };
}
